// Shared presentational helpers for long-form blog posts. Tuned for editorial
// reading (wider rhythm than the legal pages in components/legal/prose.tsx):
// 18px body, generous leading, 3xl section headings.
//
// A post body is a sequence of these inside <BlogLayout>. Keep raw <h2>/<p>
// out of post routes so every post reads the same.

import { ArrowUpRight } from "lucide-react"

import { cn, slugify } from "~/lib/utils"

/**
 * `id` defaults to a slug of the heading text, so <TableOfContents> can find
 * every section by scanning the rendered post for `h2[id]` without post
 * routes having to hand-maintain anchor ids. Pass `id` explicitly if two
 * headings in the same post would otherwise collide — or if the heading has
 * inline JSX (an <em>, a {variable}), since only a plain string child gets a
 * derived id and a heading without one silently drops out of the Contents.
 */
export function H2({
  children,
  id,
}: {
  children: React.ReactNode
  id?: string
}) {
  const headingId = id ?? (typeof children === "string" ? slugify(children) : undefined)
  return (
    <h2
      id={headingId}
      className="mt-14 scroll-mt-28 font-sans text-2xl font-semibold leading-snug tracking-tight text-ink"
    >
      {children}
    </h2>
  )
}

export function H3({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="mt-8 font-sans text-lg font-semibold text-ink">
      {children}
    </h3>
  )
}

export function P({ children }: { children: React.ReactNode }) {
  return (
    <p className="mt-5 text-[18px] leading-relaxed text-ink/75">{children}</p>
  )
}

/** Opening paragraph — slightly larger, darker, sets the post up. */
export function Lead({ children }: { children: React.ReactNode }) {
  return (
    <p className="mt-2 text-[20px] leading-relaxed text-ink/80">{children}</p>
  )
}

/** Emphasised inline phrase inside a paragraph. */
export function Strong({ children }: { children: React.ReactNode }) {
  return <strong className="font-semibold text-ink">{children}</strong>
}

/**
 * Inline link. Off-site links open in a new tab and get the site's ↗ arrow
 * (lucide ArrowUpRight, as on the Docs nav item), so a reader can tell before
 * clicking that they are leaving the article. The word joiner keeps the arrow
 * on the same line as the link's last word.
 */
export function A({
  href,
  children,
}: {
  href: string
  children: React.ReactNode
}) {
  const external = /^https?:\/\//.test(href)
  return (
    <a
      href={href}
      {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      className="font-medium text-chartreuse-deep underline underline-offset-2 transition-colors hover:text-forest"
    >
      {children}
      {external ? (
        <>
          {"\u2060"}
          <ArrowUpRight
            aria-hidden
            className="ml-0.5 inline-block h-[0.85em] w-[0.85em] align-[-0.05em]"
            strokeWidth={2}
          />
          <span className="sr-only"> (opens in a new tab)</span>
        </>
      ) : null}
    </a>
  )
}

export function List({ items }: { items: React.ReactNode[] }) {
  return (
    <ul className="mt-5 list-disc space-y-2 pl-6">
      {items.map((item, i) => (
        <li key={i} className="pl-1 text-[18px] leading-relaxed text-ink/75">
          {item}
        </li>
      ))}
    </ul>
  )
}

/**
 * Inline image with an optional caption. `src` is a path under /public.
 *
 * Pass `framed` for a product shot that already carries its own rounded
 * device frame baked into the image (e.g. the Insights/Console screenshots) —
 * it skips the border/rounding so the shot doesn't get framed twice, and
 * crops to a shared footprint so two framed shots in the same post render at
 * the same size regardless of small differences in their source aspect ratio.
 */
export function Figure({
  src,
  alt,
  caption,
  width,
  height,
  framed = false,
}: {
  src: string
  alt: string
  caption?: string
  width?: number
  height?: number
  framed?: boolean
}) {
  return (
    <figure className="mt-10">
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        loading="lazy"
        decoding="async"
        className={cn(
          "w-full",
          framed
            ? "aspect-[2806/1774] object-cover object-top"
            : "rounded-xl border border-line",
        )}
      />
      {caption ? (
        <figcaption className="mt-3 text-[14px] leading-snug text-mute">
          {caption}
        </figcaption>
      ) : null}
    </figure>
  )
}

/**
 * Looping, muted, autoplaying video with a poster frame and an animated-image
 * fallback for clients that refuse to play it. Both paths under /public.
 */
export function VideoFigure({
  src,
  poster,
  fallback,
  label,
  caption,
  width,
  height,
}: {
  src: string
  poster?: string
  fallback?: string
  label: string
  caption?: string
  width?: number
  height?: number
}) {
  return (
    <figure className="mt-10">
      <div className="overflow-hidden rounded-xl border border-line">
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          poster={poster}
          width={width}
          height={height}
          className="block w-full"
          aria-label={label}
        >
          <source src={src} type="video/mp4" />
          {fallback ? (
            <img
              src={fallback}
              alt={label}
              width={width}
              height={height}
              className="block w-full"
            />
          ) : null}
        </video>
      </div>
      {caption ? (
        <figcaption className="mt-3 text-[14px] leading-snug text-mute">
          {caption}
        </figcaption>
      ) : null}
    </figure>
  )
}

/**
 * Big centered pull quote — a full breaker to split up a long section, not an
 * inline citation (see <Turn> for interview-style attributed speech).
 */
export function Quote({
  children,
  cite,
}: {
  children: React.ReactNode
  cite?: string
}) {
  return (
    <blockquote className="mx-auto mt-12 max-w-2xl text-center">
      <p className="font-serif text-[30px] italic leading-snug text-ink sm:text-[34px]">
        “{children}”
      </p>
      {cite ? (
        <cite className="mt-4 block font-flex text-[12px] font-medium not-italic uppercase tracking-[0.14em] text-mute">
          {cite}
        </cite>
      ) : null}
    </blockquote>
  )
}

/** One turn in an interview: a speaker label, then that person's paragraphs. */
export function Turn({
  speaker,
  children,
}: {
  speaker: string
  children: React.ReactNode
}) {
  return (
    <div className="mt-8 border-l-2 border-line pl-5 [&>p:first-child]:mt-2">
      <p className="font-flex text-[12px] font-semibold uppercase tracking-[0.1em] text-moss">
        {speaker}
      </p>
      {children}
    </div>
  )
}

/** Comparison table. Scrolls sideways on narrow screens. */
export function Table({
  head,
  rows,
}: {
  head: React.ReactNode[]
  rows: React.ReactNode[][]
}) {
  return (
    <div className="mt-8 overflow-x-auto">
      <table className="w-full border-collapse text-left text-[15px] leading-snug">
        <thead>
          <tr className="border-b border-line">
            {head.map((h, i) => (
              <th
                key={i}
                className="py-3 pr-5 align-bottom font-sans font-semibold text-ink"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, r) => (
            <tr key={r} className="border-b border-line align-top">
              {row.map((cell, c) => (
                <td key={c} className="py-3 pr-5 text-ink/75">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
